// Icons
import CONTACTED from "@/assets/icons/dataPages/crm/responseStatus/contacted.svg";
import NOT_CONTACTED from "@/assets/icons/dataPages/crm/responseStatus/not_contacted.svg";
import OVERDUE from "@/assets/icons/dataPages/crm/responseStatus/overdue.svg";
import OTHER_ICON from "@/assets/icons/dataPages/crm/other.svg";
import EMAIL_ICON from "@/assets/icons/dataPages/crm/email.svg";
import PHONE_ICON from "@/assets/icons/dataPages/crm/phone.svg";
import TEXT_ICON from "@/assets/icons/dataPages/crm/text.svg";
import DMS_ICON from "@/assets/icons/dataPages/crm/dms.svg";
import WALK_IN_ICON from "@/assets/icons/dataPages/crm/walk-in.svg";
import INTERNET_ICON from "@/assets/icons/dataPages/crm/internet.svg";
import FAX_ICON from "@/assets/icons/dataPages/crm/fax.svg";
import LIST_ICON from "@/assets/icons/dataPages/crm/list.svg";

// Utils
import { getFormatUTCDate } from "@/Common/helpers/Dates";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

function parseValidationAddressComponents(
	components: ValidationAddressComponent[],
	postalAddress?: PostalAddress,
	metadata?: AddressMetadata
): ParsedAddress {
	const extractComponent = (
		addressComponents: ValidationAddressComponent[] = [],
		componentType = ""
	): ValidationAddressComponent | undefined => {
		return addressComponents.find((component) => component.componentType === componentType);
	};

	const getComponentValue = (addressComponents: ValidationAddressComponent[] = [], componentType = ""): string => {
		const component = extractComponent(addressComponents, componentType);
		return component?.componentName?.text ?? "";
	};

	const countryCode = postalAddress?.regionCode ?? "";
	const countryName = getComponentValue(components, "country");
	const zipCode = postalAddress?.postalCode ?? "";
	const city = postalAddress?.locality ?? "";
	const stateCode = postalAddress?.administrativeArea ?? "";
	const stateName = getComponentValue(components, "administrative_area_level_1") ?? stateCode;
	const streetNumber = getComponentValue(components, "street_number");
	const route = getComponentValue(components, "route");
	const address1 = `${streetNumber} ${route}`;
	const address2 = getComponentValue(components, "subpremise");

	return {
		address1,
		address2,
		city,
		stateCode,
		stateName,
		zipCode,
		countryCode,
		countryName,
		metadata,
	};
}

function parseDetailsAddressComponents(components: PlaceDetailsAddressComponent[]): Omit<ParsedAddress, "metadata"> {
	const getComponentByType = (types: string[]) => components.find((comp) => types.some((type) => comp.types.includes(type)));
	const getLongValue = (types: string[]) => getComponentByType(types)?.longText ?? "";
	const getShortValue = (types: string[]) => getComponentByType(types)?.shortText ?? "";

	const streetNumber = getLongValue(["street_number"]);
	const route = getLongValue(["route"]);
	const subpremise = getLongValue(["subpremise"]);
	const locality = getLongValue(["locality"]);
	const postalTown = getLongValue(["postal_town"]);
	const stateCode = getShortValue(["administrative_area_level_1"]);
	const stateName = getLongValue(["administrative_area_level_1"]);
	const postalCode = getLongValue(["postal_code"]);
	const countryCode = getShortValue(["country"]);
	const countryName = getLongValue(["country"]);

	const address1 = [streetNumber, route].filter(Boolean).join(" ");
	const address2 = subpremise ?? "";

	const city = locality ?? postalTown ?? "";

	return {
		address1,
		address2,
		city,
		stateCode,
		stateName,
		countryCode,
		countryName,
		zipCode: postalCode,
	};
}

function hasValue(value: unknown): boolean {
	return value !== undefined && value !== null && value !== "";
}

function getResponseStatusInfo(response: string) {
	const responseStatusMap = {
		CONTACTED: { icon: CONTACTED, title: "Prospect Contacted" },
		NOT_CONTACTED: { icon: NOT_CONTACTED, title: "Prospect Not Contacted" },
		OVERDUE: { icon: OVERDUE, title: "Prospect Overdue for Response" },
	};

	return responseStatusMap[response as keyof typeof responseStatusMap] ?? { icon: false, title: "" };
}

function getLastActivityInfo(args: { eventId: string; eventName: string; creationDateTime: Date }) {
	const { eventId, eventName, creationDateTime } = args;
	const date = getFormatUTCDate(creationDateTime, DATE_FORMAT.usDateTime12hPadded);
	const icon = getIconLastActivity(eventId);

	return { date, icon, title: eventName };
}

function getIconLastActivity(eventId: string) {
	const eventMap = {
		"14": EMAIL_ICON,
		"1": EMAIL_ICON,
		"15": PHONE_ICON,
		"2": PHONE_ICON,
		"16": TEXT_ICON,
		"13": TEXT_ICON,
	};

	return eventMap[eventId as keyof typeof eventMap] ?? OTHER_ICON;
}

const getProspectTypeIcon = (type: string) => {
	const prospectTypeMap: Record<string, string> = {
		Fax: FAX_ICON,
		List: LIST_ICON,
		Phone: PHONE_ICON,
		Email: EMAIL_ICON,
		Internet: INTERNET_ICON,
		"DMS Created": DMS_ICON,
		"Walk-In": WALK_IN_ICON,
		Other: OTHER_ICON,
	};

	return prospectTypeMap[type] || OTHER_ICON;
};

const getBackgroundColor = (index: number) => {
	return index % 2 === 0 ? "bg-gray-row-color" : "bg-white";
};

const isCustomersActiveTab = (index: number, tabs: { key: string; label: string }[] = []) => {
	const tabKey = tabs[index]?.key ?? "";
	return tabKey === "customers";
};

export {
	parseValidationAddressComponents,
	parseDetailsAddressComponents,
	hasValue,
	getResponseStatusInfo,
	getLastActivityInfo,
	getProspectTypeIcon,
	getBackgroundColor,
	isCustomersActiveTab,
};
